import { mentionToMarkerText, parseMentionMarkers } from '../markers';
import { MAX_MENTIONS_PER_REQUEST } from '../types';
import {
  createMentionChipFromMention,
  isMentionChipElement,
  readMentionFromChipElement,
} from './dom-chip';
import { serializeEditor } from './serialize';
import {
  isUnresolvedMentionElement,
  readUnresolvedMentionText,
} from './unresolved-mention';

const serializedNodeLength = (node: Node): number => {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.nodeValue ?? '').replace(/\u00a0/g, ' ').length;
  }
  if (isMentionChipElement(node)) {
    return mentionToMarkerText(readMentionFromChipElement(node)).length;
  }
  if (isUnresolvedMentionElement(node)) {
    return readUnresolvedMentionText(node).replace(/\u00a0/g, ' ').length;
  }
  if (node instanceof HTMLElement) {
    return node.tagName === 'BR' ? 1 : (node.textContent ?? '').length;
  }
  return 0;
};

export const caretLogicalOffset = (
  root: HTMLElement,
  container: Node,
  offset: number
): number => {
  if (container === root) {
    let logical = 0;
    for (let i = 0; i < offset && i < root.childNodes.length; i++) {
      logical += serializedNodeLength(root.childNodes[i]);
    }
    return logical;
  }

  let logical = 0;

  for (const child of Array.from(root.childNodes)) {
    if (child === container) {
      if (child.nodeType === Node.TEXT_NODE) {
        return logical + offset;
      }
      return logical;
    }

    if (child.contains(container)) {
      if (isMentionChipElement(child) || isUnresolvedMentionElement(child)) {
        return logical;
      }
      if (child.nodeType !== Node.TEXT_NODE && child instanceof HTMLElement) {
        return logical;
      }
    }

    logical += serializedNodeLength(child);
  }

  return logical;
};

export const setCaretToEnd = (root: HTMLElement): void => {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

export const setCaretLogicalOffset = (
  root: HTMLElement,
  logicalOffset: number
): void => {
  let remaining = Math.max(0, logicalOffset);

  for (const child of Array.from(root.childNodes)) {
    const len = serializedNodeLength(child);

    if (remaining > len) {
      remaining -= len;
      continue;
    }

    const selection = window.getSelection();
    const range = document.createRange();

    if (child.nodeType === Node.TEXT_NODE) {
      range.setStart(child, Math.min(remaining, child.nodeValue?.length ?? 0));
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
      return;
    }

    if (remaining === 0) {
      range.setStartBefore(child);
    } else {
      range.setStartAfter(child);
    }
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    return;
  }

  setCaretToEnd(root);
};

export const serializeSelection = (root: HTMLElement): string | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) {
    return null;
  }

  const { text } = serializeEditor(root);
  const start = caretLogicalOffset(
    root,
    range.startContainer,
    range.startOffset
  );
  const end = caretLogicalOffset(root, range.endContainer, range.endOffset);

  return text.slice(Math.min(start, end), Math.max(start, end));
};

const appendPlainText = (
  fragment: DocumentFragment,
  text: string
): Node | null => {
  let last: Node | null = null;
  const parts = text.split('\n');

  parts.forEach((part, index) => {
    if (part) {
      const node = document.createTextNode(part);
      fragment.appendChild(node);
      last = node;
    }
    if (index < parts.length - 1) {
      const br = document.createElement('br');
      fragment.appendChild(br);
      last = br;
    }
  });

  return last;
};

export const buildFragmentFromClipboardText = (
  text: string,
  existingMentionCount: number
): { fragment: DocumentFragment; lastNode: Node | null } => {
  const fragment = document.createDocumentFragment();
  let lastNode: Node | null = null;
  let mentionCount = existingMentionCount;

  const segments = parseMentionMarkers(text);

  segments.forEach((segment, index) => {
    if (typeof segment === 'string') {
      lastNode = appendPlainText(fragment, segment) ?? lastNode;
      return;
    }

    if (mentionCount >= MAX_MENTIONS_PER_REQUEST) {
      lastNode = appendPlainText(fragment, segment.label) ?? lastNode;
      return;
    }

    const chip = createMentionChipFromMention(segment);
    fragment.appendChild(chip);
    mentionCount += 1;

    const next = segments[index + 1];
    const nextStartsWithWhitespace =
      typeof next === 'string' && /^\s/.test(next);

    if (!nextStartsWithWhitespace) {
      const space = document.createTextNode('\u00a0');
      fragment.appendChild(space);
      lastNode = space;
    } else {
      lastNode = chip;
    }
  });

  return { fragment, lastNode };
};
